import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type {
	ClientFlowMetrics,
	ClientFlowSeriesParams,
	ClientFlowSeriesResponse,
	ListDashboardEventsParams,
	ListDashboardEventsResponse,
	ListDashboardTasksParams,
	ListDashboardTasksResponse,
	ListRecommendationsParams,
	ListRecommendationsResponse,
	OnboardingStatus,
	PortfolioBreakdown,
	PortfolioBreakdownParams,
	PublicationStats,
	PublicationStatsParams,
} from './types/dashboard'

export const dashboardApi = createApi({
	reducerPath: 'dashboardApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['DashboardTask', 'DashboardEvent', 'OnboardingStatus', 'ClientFlow', 'Recommendation'],
	endpoints: (builder) => ({
		listDashboardTasks: builder.query<ListDashboardTasksResponse, ListDashboardTasksParams | void>({
			query: (params) => ({ url: '/dashboard/tasks', params: params ?? {} }),
			providesTags: ['DashboardTask'],
		}),

		listDashboardEvents: builder.query<ListDashboardEventsResponse, ListDashboardEventsParams>({
			query: (params) => ({ url: '/dashboard/events', params }),
			providesTags: ['DashboardEvent'],
		}),

		getOnboardingStatus: builder.query<OnboardingStatus, void>({
			query: () => '/dashboard/onboarding',
			providesTags: ['OnboardingStatus'],
		}),

		getClientFlowMetrics: builder.query<ClientFlowMetrics, void>({
			query: () => '/dashboard/client-flow/metrics',
			providesTags: ['ClientFlow'],
		}),

		getClientFlowSeries: builder.query<ClientFlowSeriesResponse, ClientFlowSeriesParams>({
			query: (params) => ({ url: '/dashboard/client-flow/series', params }),
			providesTags: ['ClientFlow'],
		}),

		getPortfolioBreakdown: builder.query<PortfolioBreakdown, PortfolioBreakdownParams | void>({
			query: (params) => ({ url: '/dashboard/properties/breakdown', params: params ?? {} }),
		}),

		getPublicationStats: builder.query<PublicationStats, PublicationStatsParams>({
			query: (params) => ({ url: '/dashboard/publications', params }),
		}),

		listRecommendations: builder.query<ListRecommendationsResponse, ListRecommendationsParams | void>({
			query: (params) => ({ url: '/dashboard/recommendations', params: params ?? {} }),
			providesTags: ['Recommendation'],
		}),

		dismissRecommendation: builder.mutation<{ ok: true }, string>({
			query: (id) => ({ url: `/dashboard/recommendations/${id}/dismiss`, method: 'POST' }),
			invalidatesTags: ['Recommendation'],
		}),
	}),
})

export const {
	useListDashboardTasksQuery,
	useListDashboardEventsQuery,
	useGetOnboardingStatusQuery,
	useGetClientFlowMetricsQuery,
	useGetClientFlowSeriesQuery,
	useGetPortfolioBreakdownQuery,
	useGetPublicationStatsQuery,
	useListRecommendationsQuery,
	useDismissRecommendationMutation,
} = dashboardApi
